/**
 * useDislikesAnalytics.js
 *
 * Analytics hook for the Assessment Wizard's Screen 9 ("Dislikes").
 * Mirrors useLikesAnalytics.js exactly — same GA4 wrapper via react-ga4,
 * same IS_PRODUCTION guard, same dev console.group/table logging.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[DislikesAnalytics] ${eventName}`, "color:#C62828;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const useDislikesAnalytics = () => {
  const trackStepView = useCallback((sessionId) => {
    send("dislikes_step_view", { session_id: sessionId, step: 9 });
  }, []);

  const trackDislikeAdd = useCallback((sessionId, foodId) => {
    send("dislikes_food_add", { session_id: sessionId, food: foodId });
  }, []);

  const trackDislikeRemove = useCallback((sessionId, foodId) => {
    send("dislikes_food_remove", { session_id: sessionId, food: foodId });
  }, []);

  const trackStepNext = useCallback((sessionId, data = {}) => {
    send("dislikes_step_next", {
      session_id: sessionId,
      step: 9,
      dislikes: Array.isArray(data.dislikes) ? data.dislikes.join(",") : undefined,
      dislikes_count: Array.isArray(data.dislikes) ? data.dislikes.length : undefined,
    });
  }, []);

  const trackStepPrevious = useCallback((sessionId) => {
    send("dislikes_step_previous", { session_id: sessionId, step: 9 });
  }, []);

  const trackValidationBlocked = useCallback((sessionId, errors = {}) => {
    send("dislikes_validation_blocked", {
      session_id: sessionId,
      missing_fields: Object.keys(errors).join(","),
    });
  }, []);

  return {
    trackStepView,
    trackDislikeAdd,
    trackDislikeRemove,
    trackStepNext,
    trackStepPrevious,
    trackValidationBlocked,
  };
};

export default useDislikesAnalytics;
